import { useAuth0 } from '@auth0/auth0-react';
import classNames from 'classnames';
import { useState } from 'react';
import { Button, Card, CardBody, CardHeader, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { BackendService, Project } from '../service/backend-service';
import { useStoreDispatch } from '../store/hooks';
import { removeProject, updateProject } from '../store/store-core';
import { errorMessage } from '../util/error-utils';
import { StyledToast } from '../util/styled-toast';
import ProjectToggles from './project-toggles';

const colors: any = {
  primary: 'Blue',
  secondary: 'Grey',
  success: 'Green',
  warning: 'Yellow',
  danger: 'Red',
  info: 'Cyan',
  dark: 'Dark Grey'
}

function ProjectContainer({
  project
}: {
  project: Project
}) {
  const [loading, setLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const service = new BackendService(useAuth0());
  const [currentEditingProject, setCurrentEditingProject] = useState(project);
  let dispatch = useStoreDispatch();

  function discardChanges() {
    // Just added, we then remove it
    if (currentEditingProject.id < 0) {
      dispatch(removeProject(currentEditingProject.id));
      return;
    }

    setCurrentEditingProject(project); // goes back to the original
  }

  function applyChanges() {
    setLoading(true);
    let response: Promise<Project> = currentEditingProject.id < 0 ? service.addProject(currentEditingProject) : service.updateProject(currentEditingProject);

    response.then(savedProject => {
      dispatch(updateProject({
        id: currentEditingProject.id,
        project: savedProject
      }));
      setCurrentEditingProject(savedProject);
    })
    .catch((ex: any) => StyledToast.error(errorMessage(ex)))
    .finally(() => setLoading(false));

  }

  function toggleDeleteModal() {
    setDeleteModalOpen(!deleteModalOpen);
  }

  function deleteProject(project: Project) { 
    service.deleteProject(project)
    .then( () => {
      dispatch(removeProject(project.id))
    })
    .catch((err: any) => StyledToast.error(errorMessage(err)))
    .finally(() => {
      toggleDeleteModal();
    })
  }

  function confirmModal(project: Project) {
    return  <> 
    <Modal isOpen={deleteModalOpen} toggle={toggleDeleteModal}>
      <ModalHeader className={'alert-' + project.color} toggle={toggleDeleteModal}>Remove Project</ModalHeader>
      <ModalBody>
        Are you sure you want to remove project <b>{project.name}</b>?
      </ModalBody>
      <ModalFooter className='pt-1 pb-1'>
        <Button
          size='sm'
          color="danger"
          onClick={() => { deleteProject(project) }}>
          Remove
        </Button>
      </ModalFooter>
    </Modal>
    </>
  }

  function updateCurrentEditingProject(change: Partial<Project>) {
    setCurrentEditingProject({...currentEditingProject, ...change});
  }

  function renderBody() {
    if (loading) {
      return (
        <div className="align-items-center d-flex justify-content-center loading-spinner w-100 h-100">
          <div className={"spinner-border spinner-border-sm text-" + currentEditingProject.color} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      );
    }

    if (currentEditingProject.editing) {
      return (
        <>
          <div className="form-floating">
            <Input id="name" name="name" autoFocus placeholder="Project name ..." type="text" value={currentEditingProject.name} 
            onChange={e => updateCurrentEditingProject({ name: e.target.value })}
            onKeyDown={(e) => {
              if (e.code === "Enter") {
                applyChanges();
              }
            }}
            />
            <Label for="name"><i className='fas fa-fw fa-signature me-2' />Name</Label>
          </div>
          <div className="form-floating mt-3">
            <Input id="colorScheme" name="colorScheme" type="select"
              className={'alert-' + currentEditingProject.color}
              value={currentEditingProject.color}
              onChange={e => updateCurrentEditingProject({ color: e.target.value })}>
              {
                Object.keys(colors)
                  .map(colorKey => <option key={colorKey} value={colorKey} className={'alert-' + colorKey}>{colors[colorKey]}</option>)
              }
            </Input>
            <Label for="colorScheme"><i className='fas fa-fw fa-palette me-2' />Color Scheme</Label>
          </div>
          <div className='mt-3'>
          <ProjectToggles 
            favorite={{
              value: currentEditingProject.favorite, 
              toggle: newValue => updateCurrentEditingProject({ favorite: newValue })
            }}
            active={{
              value: currentEditingProject.active, 
              toggle: newValue => updateCurrentEditingProject({ active: newValue })
            }}
          />
          </div>
        </>
      );
    }

    return (
      <div className='row row-cols-2 row-cols-xs-1'>
        <div className='col text-center'>
          <span>
            <i className={classNames({
              'fa-fw fa-heart me-2 text-danger': true,
              'fas': currentEditingProject.favorite,
              'far': !currentEditingProject.favorite,
            })} />
            {!currentEditingProject.favorite ? 'Non-Favorite' : 'Favorite'}
          </span>
        </div>
        <div className='col text-center'>
          <span>
            <i className={classNames({
              'fa-fw fa-lightbulb me-2 text-warning': true,
              'fas': currentEditingProject.active,
              'far': !currentEditingProject.active,
            })} />
            {currentEditingProject.active ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className='col mb-3'>
      <Card>
        <CardHeader className={'alert-' + currentEditingProject.color}>
          <span className='fw-bold'>{currentEditingProject.name}</span>
          {
            currentEditingProject.editing ? <>
              <i title='Apply Changes' className='fas fa-fw fa-check cursor-pointer float-end mt-1 text-success' onClick={applyChanges} />
              <i title='Discard Changes' className='fas fa-fw fa-xmark cursor-pointer float-end mt-1 text-danger' onClick={discardChanges} />
            </> : <div className='float-end'>
              <i title='Edit Project' className='fas fa-fw fa-pen-to-square cursor-pointer' onClick={() => updateCurrentEditingProject({ editing: true })} />
            <i 
              title='Delete Project' 
              className='text-danger fas fa-fw fa-solid fa-trash-can cursor-pointer ms-1' 
              onClick={toggleDeleteModal}
            />
            {
              confirmModal(currentEditingProject)
            }
            </div>
          }
        </CardHeader>
        <CardBody>
          {
            renderBody()
          }
        </CardBody>
      </Card>
    </div>
  );
}

export default ProjectContainer;
